import {
  AfterContentInit, AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  Directive,
  forwardRef,
  Inject,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { CdkStep, CdkStepper, StepperSelectionEvent } from '@angular/cdk/stepper';
import { takeUntil } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Directionality } from '@angular/cdk/bidi';

import { AnimationCurves, AnimationDurations } from '../../core/animation/animation';
import { TabLabel } from '../directives/tab-label.directive';
import { McRAITabHeader } from './tab-header/tab-header.component';
import { McRAIHorizontalInkBar, McRAIVerticalInkBar } from '../directives/ink-bar.directive';

@Component({
  selector: 'mcrai-tab',
  template: `<ng-template><ng-content></ng-content></ng-template>
`,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class McRAITab extends CdkStep {
  @ContentChild(TabLabel) tabLabel: TabLabel;

  constructor(
    @Inject(forwardRef(() => McRAITabs)) tabs: McRAITabs,
  ) {
    super(tabs);
  }
}

@Directive({
  selector: '[mcraiTabs]',
})
export class McRAITabs extends CdkStepper implements AfterContentInit {
  @ContentChildren(McRAITab) _steps: QueryList<McRAITab>;

  protected _inkBar: any;
  @ViewChildren(McRAITabHeader) _stepHeader: QueryList<McRAITabHeader>;

  constructor(
    _dir: Directionality,
    _changeDetectorRef: ChangeDetectorRef
  ) {
    super(_dir, _changeDetectorRef);

    this.selectionChange
      .pipe(takeUntil(this._destroyed))
      .subscribe((event: StepperSelectionEvent) => {
        const activeHeader = this._stepHeader.toArray()[event.selectedIndex].getHostElement();

        this._inkBar.alignToElement(activeHeader);
      });
  }

  ngAfterContentInit() {
    this._steps.changes
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => this._stateChanged());
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    this._inkBar.alignToElement(this._stepHeader.first.getHostElement());
  }
}

@Component({
  selector: 'mcrai-horizontal-tabs',
  template: `<div class="mcrai-tabs__header">
  <mcrai-tab-header
    *ngFor="let tab of _steps; let i = index"
    (click)="tab.select()"
    [index]="i"
    [label]="tab.tabLabel || tab.label"
    [active]="selectedIndex === i"
  ></mcrai-tab-header>
  <mcrai-horizontal-ink-bar></mcrai-horizontal-ink-bar>
</div>

<div class="mcrai-tabs__content">
  <div
    *ngFor="let tab of _steps; let i = index"
    class="mcrai-tab"
    [class.mcrai-tab--active]="selectedIndex === i"
    [@stepTransition]="_getAnimationDirection(i)"
  >
    <ng-container [ngTemplateOutlet]="tab.content"></ng-container>
  </div>
</div>
`,
  styles: [`.mcrai-tabs{display:flex}.mcrai-tabs__header{display:flex;position:relative;flex:1}.mcrai-tabs__content{overflow:hidden}.mcrai-tab:not(.mcrai-tab--active){height:0;visibility:hidden}.mcrai-tabs--vertical{flex-direction:row}.mcrai-tabs--vertical .mcrai-tabs__header{flex-direction:column}.mcrai-tabs--vertical .mcrai-tabs__content{padding:0 30px;flex:3}.mcrai-tabs--horizontal{flex-direction:column}.mcrai-tabs--horizontal .mcrai-tabs__header{flex-direction:row;flex:0 0 45px;align-items:center;justify-content:flex-start}.mcrai-tabs--horizontal .mcrai-tabs__content{padding:30px 0;flex:1}.mcrai-ink-bar{position:absolute;transition:.4s;transition-timing-function:cubic-bezier(.25,.8,.25,1)}.mcrai-ink-bar--horizontal{height:2px;bottom:0}.mcrai-ink-bar--vertical{width:2px;right:0}`],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: McRAITabs, useExisting: McRAIHorizontalTabs }],
  animations: [
    trigger('stepTransition', [
      state('previous', style({
        transform: 'translate3d(-100%, 0, 0)',
        visibility: 'hidden'
      })),
      state('current', style({
        transform: 'none',
        visibility: 'visible'
      })),
      state('next', style({
        transform: 'translate3d(100%, 0, 0)',
        visibility: 'hidden'
      })),
      transition('* => *',
        animate(`${AnimationDurations.COMPLEX} ${AnimationCurves.DECELERATION_CURVE}`))
    ])
  ],
  host: {
    '[class.mcrai-tabs]': 'true',
    '[class.mcrai-tabs--horizontal]': 'true'
  }
})
export class McRAIHorizontalTabs extends McRAITabs implements AfterViewInit {
  @ViewChild(McRAIHorizontalInkBar) protected _inkBar: McRAIHorizontalInkBar;
}

@Component({
  selector: 'mcrai-vertical-tabs',
  template: `<div class="mcrai-tabs__header">
  <mcrai-tab-header
    *ngFor="let tab of _steps; let i = index"
    (click)="tab.select()"
    [index]="i"
    [label]="tab.tabLabel || tab.label"
    [active]="selectedIndex === i"
  ></mcrai-tab-header>
  <mcrai-vertical-ink-bar></mcrai-vertical-ink-bar>
</div>

<div class="mcrai-tabs__content">
  <div
    *ngFor="let tab of _steps; let i = index"
    class="mcrai-tab"
    [class.mcrai-tab--active]="selectedIndex === i"
    [@stepTransition]="_getAnimationDirection(i)"
  >
    <ng-container [ngTemplateOutlet]="tab.content"></ng-container>
  </div>
</div>
`,
  styles: [`.mcrai-tabs{display:flex}.mcrai-tabs__header{display:flex;position:relative;flex:1}.mcrai-tabs__content{overflow:hidden}.mcrai-tab:not(.mcrai-tab--active){height:0;visibility:hidden}.mcrai-tabs--vertical{flex-direction:row}.mcrai-tabs--vertical .mcrai-tabs__header{flex-direction:column}.mcrai-tabs--vertical .mcrai-tabs__content{padding:0 30px;flex:3}.mcrai-tabs--horizontal{flex-direction:column}.mcrai-tabs--horizontal .mcrai-tabs__header{flex-direction:row;flex:0 0 45px;align-items:center;justify-content:flex-start}.mcrai-tabs--horizontal .mcrai-tabs__content{padding:30px 0;flex:1}.mcrai-ink-bar{position:absolute;transition:.4s;transition-timing-function:cubic-bezier(.25,.8,.25,1)}.mcrai-ink-bar--horizontal{height:2px;bottom:0}.mcrai-ink-bar--vertical{width:2px;right:0}`],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: McRAITabs, useExisting: McRAIVerticalTabs }],
  animations: [
    trigger('stepTransition', [
      state('previous', style({
        transform: 'translate3d(-100%, 0, 0)',
        visibility: 'hidden'
      })),
      state('current', style({
        transform: 'none',
        visibility: 'visible'
      })),
      state('next', style({
        transform: 'translate3d(100%, 0, 0)',
        visibility: 'hidden'
      })),
      transition('* => *',
        animate(`${AnimationDurations.COMPLEX} ${AnimationCurves.DECELERATION_CURVE}`))
    ])
  ],
  host: {
    '[class.mcrai-tabs]': 'true',
    '[class.mcrai-tabs--vertical]': 'true'
  }
})
export class McRAIVerticalTabs extends McRAITabs implements AfterViewInit {
  @ViewChild(McRAIVerticalInkBar) protected _inkBar: McRAIVerticalInkBar;
}
